/**
 * A kind of companion API to ./feed.ts. See that file for more info.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {AppState} from 'react-native'
import {type ISODatetimeString} from '@atproto/syntax'
import {useQueryClient} from '@tanstack/react-query'
import {EventEmitter} from 'eventemitter3'

import BroadcastChannel from '#/lib/broadcast'
import {resetBadgeCount} from '#/lib/notifications/notifications'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {truncateAndInvalidate} from '#/state/queries/util'
import {useAppviewClient, useSession} from '#/state/session'
import {app} from '#/lexicons'
import {RQKEY as RQKEY_NOTIFS} from './feed'
import {type CachedFeedPage, type FeedPage, type UnreadCheck} from './types'
import {fetchPage, markUnreadCheckSeen, summarizeUnreadCheck} from './util'

const UPDATE_INTERVAL = 30 * 1e3 // 30sec

const broadcast = new BroadcastChannel('NOTIFS_BROADCAST_CHANNEL')

const emitter = new EventEmitter()

type StateContext = string

interface ApiContext {
  /**
   * Marks notifications as seen on the server, up to `seenAt` if given,
   * otherwise up to the last unread check.
   */
  markAllRead: (opts?: {seenAt?: Date}) => Promise<void>
  checkUnread: (opts?: {
    invalidate?: boolean
    isPoll?: boolean
  }) => Promise<void>
  getCachedUnreadPage: () => FeedPage | undefined
}

const stateContext = createContext<StateContext>('')
stateContext.displayName = 'NotificationsUnreadStateContext'

const lastCheckContext = createContext<UnreadCheck | undefined>(undefined)
lastCheckContext.displayName = 'NotificationsUnreadLastCheckContext'

const apiContext = createContext<ApiContext>({
  async markAllRead() {},
  async checkUnread() {},
  getCachedUnreadPage: () => undefined,
})
apiContext.displayName = 'NotificationsUnreadApiContext'

export function Provider({children}: React.PropsWithChildren<{}>) {
  const {hasSession} = useSession()
  const client = useAppviewClient()
  const queryClient = useQueryClient()
  const moderationOpts = useModerationOpts()

  const [numUnread, setNumUnread] = useState('')
  const [lastCheck, setLastCheck] = useState<UnreadCheck>()

  const checkUnreadRef = useRef<ApiContext['checkUnread'] | null>(null)
  const cacheRef = useRef<CachedFeedPage>({
    usableInFeed: false,
    syncedAt: new Date(),
    data: undefined,
    unreadCount: 0,
  })

  useEffect(() => {
    function markAsUnusable() {
      if (cacheRef.current) {
        cacheRef.current.usableInFeed = false
      }
    }
    emitter.addListener('invalidate', markAsUnusable)
    return () => {
      emitter.removeListener('invalidate', markAsUnusable)
    }
  }, [])

  // periodic sync
  useEffect(() => {
    if (!hasSession || !checkUnreadRef.current) {
      return
    }
    checkUnreadRef.current() // fire on init
    const interval = setInterval(
      () => checkUnreadRef.current?.({isPoll: true}),
      UPDATE_INTERVAL,
    )
    return () => clearInterval(interval)
  }, [hasSession])

  // listen for broadcasts
  useEffect(() => {
    const listener = ({data}: MessageEvent) => {
      cacheRef.current = {
        usableInFeed: false,
        syncedAt: new Date(),
        data: undefined,
        unreadCount:
          data.event === '30+'
            ? 30
            : data.event === ''
              ? 0
              : parseInt(data.event, 10) || 1,
      }
      setNumUnread(data.event)
    }
    broadcast.addEventListener('message', listener)
    return () => {
      broadcast.removeEventListener('message', listener)
    }
  }, [setNumUnread])

  const isFetchingRef = useRef(false)
  /**
   * When notifications were last marked as seen, in ms since the epoch. A
   * check asked before then may count notifications that have been seen since.
   */
  const lastMarkedReadAt = useRef(0)

  // create API
  const api = useMemo<ApiContext>(() => {
    return {
      async markAllRead({seenAt = cacheRef.current.syncedAt} = {}) {
        // update server
        await client.call(app.bsky.notification.updateSeen, {
          // toISOString always emits the Z-suffixed form the format requires
          seenAt: seenAt.toISOString() as ISODatetimeString,
        })
        lastMarkedReadAt.current = Date.now()

        /*
         * Nothing is unread any more, so polling goes back to its usual rate,
         * and the page from the last check is out of date.
         */
        cacheRef.current = {
          ...cacheRef.current,
          unreadCount: 0,
          usableInFeed: false,
        }

        // update & broadcast
        setNumUnread('')
        setLastCheck(check => markUnreadCheckSeen(check, seenAt.getTime()))
        broadcast.postMessage({event: ''})
        resetBadgeCount()
      },

      async checkUnread({
        invalidate,
        isPoll,
      }: {invalidate?: boolean; isPoll?: boolean} = {}) {
        try {
          if (!hasSession) return
          if (AppState.currentState !== 'active') {
            return
          }

          // reduce polling if unread count is set
          if (isPoll && cacheRef.current?.unreadCount !== 0) {
            // if hit 30+ then don't poll, otherwise reduce polling by 50%
            if (cacheRef.current?.unreadCount >= 30 || Math.random() >= 0.5) {
              return
            }
          }

          if (isFetchingRef.current) {
            return
          }
          // Do not move this without ensuring it gets a symmetrical reset in the finally block.
          isFetchingRef.current = true

          // count
          const requestedAt = Date.now()
          const {page, indexedAt: lastIndexed} = await fetchPage({
            client,
            cursor: undefined,
            limit: 40,
            queryClient,
            moderationOpts,
            reasons: [],

            // only fetch subjects when the page is going to be used
            // in the notifications query, otherwise skip it
            fetchAdditionalData: !!invalidate,
          })
          /*
           * Marked read while this was in flight, so the count may be stale.
           * The next check will catch up. A check that loads into the feed
           * goes ahead, since that feed marks what it shows as read anyway.
           */
          if (!invalidate && requestedAt < lastMarkedReadAt.current) {
            return
          }
          const unreadCount = countUnread(page)
          const unreadCountStr =
            unreadCount >= 30
              ? '30+'
              : unreadCount === 0
                ? ''
                : String(unreadCount)

          // track last sync
          const now = new Date()
          const lastIndexedDate = lastIndexed
            ? new Date(lastIndexed)
            : undefined
          cacheRef.current = {
            usableInFeed: !!invalidate, // will be used immediately
            data: page,
            syncedAt:
              !lastIndexedDate || now > lastIndexedDate ? now : lastIndexedDate,
            unreadCount,
          }

          // update & broadcast
          setNumUnread(unreadCountStr)
          setLastCheck(summarizeUnreadCheck(page, requestedAt))
          if (invalidate) {
            truncateAndInvalidate(queryClient, RQKEY_NOTIFS('all'))
            truncateAndInvalidate(queryClient, RQKEY_NOTIFS('mentions'))
          }
          broadcast.postMessage({event: unreadCountStr})
        } finally {
          isFetchingRef.current = false
        }
      },

      getCachedUnreadPage() {
        // return cached page if it's marked as fresh enough
        if (cacheRef.current.usableInFeed) {
          return cacheRef.current.data
        }
      },
    }
  }, [setNumUnread, queryClient, moderationOpts, client, hasSession])
  checkUnreadRef.current = api.checkUnread

  return (
    <stateContext.Provider value={numUnread}>
      <lastCheckContext.Provider value={lastCheck}>
        <apiContext.Provider value={api}>{children}</apiContext.Provider>
      </lastCheckContext.Provider>
    </stateContext.Provider>
  )
}

export function useUnreadNotifications() {
  return useContext(stateContext)
}

/**
 * What the last unread check found, or undefined before the first one.
 * Updated on every check, and whenever notifications are marked read.
 */
export function useLastUnreadCheck() {
  return useContext(lastCheckContext)
}

export function useUnreadNotificationsApi() {
  return useContext(apiContext)
}

function countUnread(page: FeedPage) {
  let num = 0
  for (const item of page.items) {
    if (!item.notification.isRead) {
      num++
    }
    if (item.additional) {
      for (const item2 of item.additional) {
        if (!item2.isRead) {
          num++
        }
      }
    }
  }
  return num
}

export function invalidateCachedUnreadPage() {
  emitter.emit('invalidate')
}
